// ─────────────────────────────────────────────────────────────────────────────
// HOW A LEG READS ON SCREEN.
//
// The engine returns one of four states per leg, and the only job of this file is to make
// sure none of them is rendered as another. The states, from chart-check.mjs:
//
//   ATTENTION    a check we could make found something
//   TO_READ      there is work here, and nothing has judged it — the progress notes
//   CLEAR        we looked and there is nothing to raise
//   NOT_CHECKED  we could not look
//
// ── THE TWO WAYS THIS SCREEN BECOMES WORSE THAN NOT HAVING IT ────────────────
//
// 1. NOT_CHECKED RENDERED AS CLEAR. The labs leg is NOT_CHECKED on every chart in the
//    building, and her condition for accepting a two-leg version was that the labs column
//    "says it is not being checked rather than rendering clean". A tick there is a product
//    telling a unit manager her labs are reviewed when nothing has looked at them.
// 2. NOT_CHECKED RENDERED AS A FAILURE. The other direction is almost as bad and it is the
//    one that kills adoption: a column of red on every chart, every morning, for a scope
//    we have not been granted. She warned twice that overwhelm leads to "non-usage or
//    under-usage". Red is for a finding; blocked is its own thing and reads as grey-amber.
//
// So the mark is its own state, with its own glyph, its own word, and its own colour, and
// the test asserts it shares none of the three with either neighbour.
//
// Deliberately a plain module with no React in it: the row, the column header and the
// facility-level summary all have to agree about what a state looks like, and the only way
// to guarantee that is for all three to call this.
// ─────────────────────────────────────────────────────────────────────────────

export const LEG_STATE = {
  ATTENTION: "ATTENTION",
  TO_READ: "TO_READ",
  CLEAR: "CLEAR",
  NOT_CHECKED: "NOT_CHECKED",
};

/** The order the states are explained in: worst first, blocked last. */
export const LEG_STATE_ORDER = [
  LEG_STATE.ATTENTION,
  LEG_STATE.TO_READ,
  LEG_STATE.CLEAR,
  LEG_STATE.NOT_CHECKED,
];

const MARKS = {
  [LEG_STATE.ATTENTION]: {
    state: LEG_STATE.ATTENTION,
    glyph: "!",
    word: "Look",
    meaning: "a check found something",
    tone: { bg: "#fee2e2", fg: "#7f1d1d", border: "#fca5a5" },
    isFinding: true,
    wasChecked: true,
  },
  [LEG_STATE.TO_READ]: {
    state: LEG_STATE.TO_READ,
    glyph: "○",
    word: "Read",
    meaning: "there is something here to read; nothing has judged it",
    tone: { bg: "#eff6ff", fg: "#1e40af", border: "#bfdbfe" },
    isFinding: false,
    wasChecked: true,
  },
  [LEG_STATE.CLEAR]: {
    state: LEG_STATE.CLEAR,
    glyph: "✓",
    word: "Clear",
    meaning: "we looked and there is nothing to raise",
    tone: { bg: "#dcfce7", fg: "#14532d", border: "#86efac" },
    isFinding: false,
    wasChecked: true,
  },
  [LEG_STATE.NOT_CHECKED]: {
    state: LEG_STATE.NOT_CHECKED,
    glyph: "⊘",
    // NOT "No" and NOT "Fail". The word a unit manager reads has to say that the gap is
    // OURS. "Not checked" is the whole sentence her condition for shipping was about.
    word: "Not checked",
    meaning: "we could not look — this is our gap, not the chart's",
    // AMBER, WHICH IS WHAT THIS FILE SAID IT WAS ALL ALONG. The comment at the top has
    // read "blocked is its own thing and reads as grey-amber" since the first version,
    // and the colour that shipped was violet. Nobody noticed because every test asked
    // only whether this tone DIFFERS from the other three, which violet does -- a
    // distinctness check cannot tell you the colour is the wrong one, only that it is not
    // one of its neighbours.
    //
    // It matters more than a palette preference. Violet carries no meaning to a unit
    // manager, so the column read as decoration; amber reads as "held up", which is what
    // a scope we have not been granted actually is. Red still belongs to a finding -- the
    // whole reason this state exists is that a column of red every morning for OUR gap is
    // the thing that kills adoption.
    tone: { bg: "#fef3c7", fg: "#713f12", border: "#fcd34d" },
    isFinding: false,
    wasChecked: false,
  },
};

/**
 * The mark for a leg state.
 *
 * AN UNRECOGNISED STATE IS NOT_CHECKED, never CLEAR. A state this build does not know — a
 * server ahead of the bundle, a null, a typo — is a thing we cannot say was checked, and the
 * only safe way to render it is as the reader's work. The alternative is a screen that shows
 * a green tick for a value it could not parse.
 */
export function legMark(state) {
  return MARKS[state] || MARKS[LEG_STATE.NOT_CHECKED];
}

/**
 * What the facility-level summary says, in one sentence.
 *
 * THREE DIFFERENT SENTENCES, NOT ONE WITH A NUMBER IN IT. "8 charts, 3 need attention" is a
 * morning's work. "No charts had activity" is a quiet night. "We have no baseline yet" and
 * "the scan has not run since Tuesday" are OUR problems and must not be said in the words of
 * either of the first two — which is the whole "two states are usually three" rule applied to
 * the one line at the top of the screen.
 */
export function chartCheckHeadline(data, { staleAfterHours = 36 } = {}) {
  if (!data) return { tone: "unknown", text: "Nothing has been loaded yet." };
  const c = data.counts || {};
  const n = c.residents || 0;
  const stale = Number(data.freshness?.hoursSinceNewestOrder);

  if (data.baseline === false) {
    return {
      tone: "unknown",
      text:
        "This is every order we currently hold for the building, not 24 hours of new ones. " +
        "We have not held this facility's orders long enough to tell what is new — the first " +
        "scan sees everything for the first time. It settles after a night.",
    };
  }
  if (Number.isFinite(stale) && stale > staleAfterHours) {
    return {
      tone: "unknown",
      text:
        `The newest order we hold is ${Math.round(stale)} hours old, which is older than the ` +
        "window. This list is what the last scan saw, not what is on the chart now.",
    };
  }
  if (!n) {
    return {
      tone: "quiet",
      // "a medication or order", not "an order": the worklist reads the medications/orders
      // list, which is one route of entry carrying both — and a quiet window for THAT is not a
      // quiet window for every order on the chart. The rest arrives November 2026.
      text:
        `No resident had a medication or order written or a progress note added in the last ` +
        `${data.window?.hours ?? 24} hours.`,
    };
  }
  const bits = [`${n} ${n === 1 ? "chart" : "charts"} with activity`];
  bits.push(`${c.needingAttention || 0} ${(c.needingAttention || 0) === 1 ? "needs" : "need"} attention`);
  if (c.notesToRead) bits.push(`${c.notesToRead} ${c.notesToRead === 1 ? "note" : "notes"} to read`);
  // ── THE NUMBER SHE OPENS THE SCREEN FOR AT 10AM ───────────────────────────
  //
  // Not shown until something has been ticked. "8 left" beside "8 charts" on an untouched
  // list is the same number said twice, which is noise on a screen whose named risk is
  // overwhelm; the moment one is ticked it becomes the only number that is changing.
  const left = c.remaining;
  const done = c.reviewed || 0;
  if (done > 0 && Number.isFinite(left)) {
    bits.push(left === 0 ? "all reviewed" : `${left} left`);
  }
  return {
    // ALL WORKED IS NOT "QUIET". A quiet window is a building with no activity; this is a
    // building with activity that somebody has been through, which is the opposite
    // achievement and deserves its own colour rather than borrowing either.
    tone: done > 0 && left === 0 ? "done" : c.needingAttention ? "work" : "quiet",
    text: `${bits.join(" · ")}.`,
  };
}

/**
 * The resident's display name, or the id.
 *
 * A NAME WE DO NOT RECOGNISE IS NOT A MISSING RESIDENT. The chart check returns
 * PointClickCare patient ids and nothing else — no name leaves the server, deliberately —
 * and the roster this screen already holds is where names come from. When the roster has no
 * row for an id, the id is shown as-is rather than "Unknown resident": the resident is on
 * the chart check because they have an order, so they exist, and the thing we are missing is
 * our own roster row.
 */
export function residentLabel(residentId, roster) {
  const id = String(residentId);
  const hit = (roster || []).find(
    (r) => String(r.resident_id ?? r.residentId ?? r.id ?? "") === id,
  );
  const name = hit && (hit.name || hit.residentName);
  return {
    name: name || id,
    room: (hit && (hit.room || hit.roomNumber)) || null,
    // TRUE when we are showing the raw id. The screen says so, because an id where every
    // other row has a name reads as a bug rather than as a roster gap.
    unresolved: !name,
  };
}

// ── WHAT WE ALREADY FOUND, SAID WITHOUT A SECOND CLICK ───────────────────────
//
// The row started fully collapsed, on the reasoning that eight charts times three legs is
// twenty-four checks and showing all of them at once is a wall. That reasoning holds for the
// BLOCKED questions and the ones that passed — nobody needs to be told four times a morning
// that labs are not granted.
//
// It does not hold for the findings. A check that found something is the entire reason the
// chart is on the list, we already have the sentence, and making her open each row to read
// what we had already reviewed out is a click that buys nothing. So the ATTENTION checks print
// inline and everything else stays behind the row.
//
// Named orders, not a count. "1 of 1 new order is missing something" sends her to the chart
// to find out which; "furosemide 20 mg tablet — missing a route" is the finding.
//
// `wants` arrives from HIGH_RISK_CLASSES already carrying its article ("a blood-pressure or
// pulse hold"), so the sentence is built around it rather than in front of it: the first
// version read "no a blood-pressure or pulse hold stated", which was visible on the very
// first screenshot and is the kind of thing a unit manager reads as the product being
// slapdash. Here rather than inline in the component so it has a test.
export function findingLines(leg) {
  const out = [];
  for (const c of leg?.checks || []) {
    if (c.state !== LEG_STATE.ATTENTION) continue;
    const e = c.evidence || {};
    for (const x of e.incomplete || []) out.push(`${x.order} — missing ${x.missing.join(", ")}`);
    for (const x of e.withoutHold || []) out.push(`${x.order} — ${x.wants} is not stated`);
    for (const x of e.overlaps || []) {
      out.push(
        `${x.a.order} (pain ${x.a.range.lo}–${x.a.range.hi}) and ${x.b.order} ` +
        `(${x.b.range.lo}–${x.b.range.hi}) both cover a pain of ${x.ambiguousScores.join(" or ")}`,
      );
    }
    // A check that found something and carried no named evidence still has its sentence,
    // which is better than silence — the alternative is a red mark with nothing beside it.
    if (!(e.incomplete || e.withoutHold || e.overlaps)) out.push(c.because);
  }
  return out;
}

// ── WHICH CHARTS THE NIGHTLY ALERT WOULD REPORT ──────────────────────────────
//
// The two checks the nightly digest counts, by id. A chart carrying one of them is a chart
// that reaches somebody's inbox for up to seven days — so it is the only chart worth asking
// "what happened?" about, and the disposition picker appears on those and nowhere else.
//
// Asked by ID RATHER THAN BY STATE. The high-risk check is deliberately TO_READ and not
// ATTENTION — a new order in a high-alert class is a chart to read, not a finding, and making
// it amber turned the attention count into the row count. So a state test would miss exactly
// half of what the alert reports, which is the quiet mismatch this names to avoid.
export const ALERT_CHECK_IDS = ["order-without-note", "high-risk"];

/** Does this row carry a finding the nightly alert reports? */
export function alertWouldReport(row) {
  const legs = row?.legs || {};
  return Object.keys(legs).some((k) =>
    (legs[k]?.checks || []).some((c) => ALERT_CHECK_IDS.includes(c?.id)),
  );
}
